import React from 'react';
import { 
    CoRegulateLogo, 
    CoRegulateMark, 
    CoRegulateMarkLoops,
    CoRegulateMarkCircles,
    CoRegulateMarkAnimated,
    CoRegulateFavicon 
} from '../components/Logo';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BrandShowcase = () => {
    const navigate = useNavigate();
    
    const colorPalette = {
        primary: [
            { name: 'Sage Green', hex: '#7D8E95', usage: 'Primary actions' },
            { name: 'Eucalyptus', hex: '#2A8585', usage: 'Hover states' },
            { name: 'Mist Blue', hex: '#7D8E95', usage: 'Secondary actions' },
            { name: 'Slate Blue', hex: '#7A8CA5', usage: 'Accents, icons' }
        ],
        neutrals: [
            { name: 'Warm Oat', hex: '#FBE0C3', usage: 'Primary background' },
            { name: 'Soft Sand', hex: '#FFBB98', usage: 'Secondary background' },
            { name: 'Warm Grey', hex: '#7D8E95', usage: 'Borders' }
        ],
        accents: [
            { name: 'Dusty Lavender', hex: '#7D8E95', usage: 'Communication' },
            { name: 'Muted Rose', hex: '#FFBB98', usage: 'Co-regulation' },
            { name: 'Soft Gold', hex: '#E4D3A3', usage: 'Highlights' }
        ],
        text: [
            { name: 'Deep Warm Grey', hex: '#1D6B6B', usage: 'Primary text' },
            { name: 'Soft Charcoal', hex: '#2A8585', usage: 'Secondary text' }
        ]
    };
    
    return (
        <div className="min-h-screen py-12 px-6" style={{ background: '#FBE0C3' }}>
            <div className="max-w-4xl mx-auto">
                {/* Back button */}
                <button 
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 mb-10 transition-gentle"
                    style={{ color: '#2A8585' }}
                >
                    <ArrowLeft className="w-5 h-5" />
                    <span>Back</span>
                </button>
                
                {/* Header */}
                <div className="text-center mb-16">
                    <h1 className="font-heading text-3xl md:text-4xl font-bold mb-4" style={{ color: '#1D6B6B' }}>
                        CoRegulate Brand Assets
                    </h1>
                    <p className="text-lg max-w-xl mx-auto" style={{ color: '#2A8585' }}>
                        Logo system designed for ADHD hearts and nervous systems. 
                        Gentle, trauma-informed, and emotionally safe.
                    </p>
                </div>

                {/* Primary Logo */}
                <section className="mb-16">
                    <h2 className="font-heading text-xl font-semibold mb-6" style={{ color: '#1D6B6B' }}>
                        Primary Logo
                    </h2>
                    <Card className="border-0 rounded-3xl overflow-hidden" style={{ boxShadow: '0 8px 30px rgba(74, 74, 71, 0.04)' }}>
                        <CardContent className="p-12 flex flex-col items-center justify-center" style={{ background: 'linear-gradient(135deg, #FBE0C3 0%, #FFBB98 100%)' }}>
                            <CoRegulateLogo size="xl" showTagline />
                        </CardContent>
                    </Card>
                </section>

                {/* Logo Variants */}
                <section className="mb-16">
                    <h2 className="font-heading text-xl font-semibold mb-6" style={{ color: '#1D6B6B' }}>
                        Logo Variants
                    </h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        <Card className="border-0 rounded-2xl" style={{ boxShadow: '0 4px 20px rgba(74, 74, 71, 0.04)', background: '#FEF6EF' }}>
                            <CardContent className="p-8 flex flex-col items-center">
                                <p className="text-sm mb-4" style={{ color: '#2A8585' }}>Horizontal</p>
                                <CoRegulateLogo size="md" />
                            </CardContent>
                        </Card>
                        
                        <Card className="border-0 rounded-2xl" style={{ boxShadow: '0 4px 20px rgba(74, 74, 71, 0.04)', background: '#FEF6EF' }}>
                            <CardContent className="p-8 flex flex-col items-center">
                                <p className="text-sm mb-4" style={{ color: '#2A8585' }}>Stacked</p>
                                <CoRegulateLogo size="md" variant="stacked" />
                            </CardContent>
                        </Card>
                    </div>
                </section>

                {/* Logo Mark Options */}
                <section className="mb-16">
                    <h2 className="font-heading text-xl font-semibold mb-6" style={{ color: '#1D6B6B' }}>
                        Logo Mark Options
                    </h2>
                    <p className="mb-6" style={{ color: '#2A8585' }}>
                        Three symbolic approaches, each representing co-regulation and connection.
                    </p>
                    <div className="grid md:grid-cols-3 gap-6">
                        <Card className="border-0 rounded-2xl" style={{ boxShadow: '0 4px 20px rgba(74, 74, 71, 0.04)', background: '#FEF6EF' }}>
                            <CardContent className="p-8 flex flex-col items-center">
                                <div className="w-24 h-24 rounded-2xl flex items-center justify-center mb-4" style={{ background: '#FBE0C3' }}>
                                    <CoRegulateMark size={64} />
                                </div>
                                <p className="font-medium text-center" style={{ color: '#1D6B6B' }}>Converging Curves</p>
                                <p className="text-sm text-center mt-1" style={{ color: '#2A8585' }}>Two paths meeting</p>
                            </CardContent>
                        </Card>
                        
                        <Card className="border-0 rounded-2xl" style={{ boxShadow: '0 4px 20px rgba(74, 74, 71, 0.04)', background: '#FEF6EF' }}>
                            <CardContent className="p-8 flex flex-col items-center">
                                <div className="w-24 h-24 rounded-2xl flex items-center justify-center mb-4" style={{ background: '#FBE0C3' }}>
                                    <CoRegulateMarkLoops size={64} />
                                </div>
                                <p className="font-medium text-center" style={{ color: '#1D6B6B' }}>Interlocking Loops</p>
                                <p className="text-sm text-center mt-1" style={{ color: '#2A8585' }}>Continuous connection</p>
                            </CardContent>
                        </Card>
                        
                        <Card className="border-0 rounded-2xl" style={{ boxShadow: '0 4px 20px rgba(74, 74, 71, 0.04)', background: '#FEF6EF' }}>
                            <CardContent className="p-8 flex flex-col items-center">
                                <div className="w-24 h-24 rounded-2xl flex items-center justify-center mb-4" style={{ background: '#FBE0C3' }}>
                                    <CoRegulateMarkCircles size={64} />
                                </div>
                                <p className="font-medium text-center" style={{ color: '#1D6B6B' }}>Connected Circles</p>
                                <p className="text-sm text-center mt-1" style={{ color: '#2A8585' }}>Two nervous systems</p>
                            </CardContent>
                        </Card>
                    </div>
                </section>

                {/* Color Palette */}
                <section className="mb-16">
                    <h2 className="font-heading text-xl font-semibold mb-6" style={{ color: '#1D6B6B' }}>
                        Color Palette
                    </h2>
                    
                    {/* Primary Colors */}
                    <div className="mb-8">
                        <h3 className="text-sm font-medium mb-4" style={{ color: '#2A8585' }}>Primary Colors</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {colorPalette.primary.map(color => (
                                <div key={color.hex}>
                                    <div className="w-full h-20 rounded-xl mb-3" style={{ background: color.hex }} />
                                    <p className="font-medium" style={{ color: '#1D6B6B' }}>{color.name}</p>
                                    <p className="text-sm" style={{ color: '#2A8585' }}>{color.hex}</p>
                                    <p className="text-xs mt-1" style={{ color: '#7D8E95' }}>{color.usage}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    {/* Neutrals */}
                    <div className="mb-8">
                        <h3 className="text-sm font-medium mb-4" style={{ color: '#2A8585' }}>Neutrals</h3>
                        <div className="grid grid-cols-3 gap-4">
                            {colorPalette.neutrals.map(color => (
                                <div key={color.hex}>
                                    <div className="w-full h-20 rounded-xl mb-3 border" style={{ background: color.hex, borderColor: '#7D8E95' }} />
                                    <p className="font-medium" style={{ color: '#1D6B6B' }}>{color.name}</p>
                                    <p className="text-sm" style={{ color: '#2A8585' }}>{color.hex}</p>
                                    <p className="text-xs mt-1" style={{ color: '#7D8E95' }}>{color.usage}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    {/* Accents */}
                    <div className="mb-8">
                        <h3 className="text-sm font-medium mb-4" style={{ color: '#2A8585' }}>Accents (Emotional Warmth)</h3>
                        <div className="grid grid-cols-3 gap-4">
                            {colorPalette.accents.map(color => (
                                <div key={color.hex}>
                                    <div className="w-full h-20 rounded-xl mb-3" style={{ background: color.hex }} />
                                    <p className="font-medium" style={{ color: '#1D6B6B' }}>{color.name}</p>
                                    <p className="text-sm" style={{ color: '#2A8585' }}>{color.hex}</p>
                                    <p className="text-xs mt-1" style={{ color: '#7D8E95' }}>{color.usage}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    {/* Text Colors */}
                    <div>
                        <h3 className="text-sm font-medium mb-4" style={{ color: '#2A8585' }}>Text Colors</h3>
                        <div className="grid grid-cols-2 gap-4">
                            {colorPalette.text.map(color => (
                                <div key={color.hex}>
                                    <div className="w-full h-20 rounded-xl mb-3" style={{ background: color.hex }} />
                                    <p className="font-medium" style={{ color: '#1D6B6B' }}>{color.name}</p>
                                    <p className="text-sm" style={{ color: '#2A8585' }}>{color.hex}</p>
                                    <p className="text-xs mt-1" style={{ color: '#7D8E95' }}>{color.usage}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Animated & Favicon */}
                <section className="mb-16">
                    <h2 className="font-heading text-xl font-semibold mb-6" style={{ color: '#1D6B6B' }}>
                        Special Versions
                    </h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        <Card className="border-0 rounded-2xl" style={{ boxShadow: '0 4px 20px rgba(74, 74, 71, 0.04)', background: '#FEF6EF' }}>
                            <CardContent className="p-8 flex flex-col items-center">
                                <p className="text-sm mb-4" style={{ color: '#2A8585' }}>Animated (Loading State)</p>
                                <div className="w-24 h-24 rounded-2xl flex items-center justify-center" style={{ background: '#FBE0C3' }}>
                                    <CoRegulateMarkAnimated size={64} />
                                </div>
                                <p className="text-xs mt-4" style={{ color: '#7D8E95' }}>Gentle breathing animation</p>
                            </CardContent>
                        </Card>
                        
                        <Card className="border-0 rounded-2xl" style={{ boxShadow: '0 4px 20px rgba(74, 74, 71, 0.04)', background: '#FEF6EF' }}>
                            <CardContent className="p-8 flex flex-col items-center">
                                <p className="text-sm mb-4" style={{ color: '#2A8585' }}>Favicon / App Icon</p>
                                <div className="flex items-center gap-4">
                                    <CoRegulateFavicon size={16} />
                                    <CoRegulateFavicon size={32} />
                                    <CoRegulateFavicon size={48} />
                                    <CoRegulateFavicon size={64} />
                                </div>
                                <p className="text-xs mt-4" style={{ color: '#7D8E95' }}>Simplified for small sizes</p>
                            </CardContent>
                        </Card>
                    </div>
                </section>

                {/* Usage Guidelines */}
                <section className="mb-16">
                    <h2 className="font-heading text-xl font-semibold mb-6" style={{ color: '#1D6B6B' }}>
                        Usage Guidelines
                    </h2>
                    <Card className="border-0 rounded-2xl" style={{ boxShadow: '0 4px 20px rgba(74, 74, 71, 0.04)', background: '#FEF6EF' }}>
                        <CardContent className="p-8">
                            <div className="space-y-6">
                                <div>
                                    <h3 className="font-medium mb-2" style={{ color: '#1D6B6B' }}>Backgrounds</h3>
                                    <p style={{ color: '#2A8585' }}>
                                        Use Warm Oat (#FBE0C3) or Soft Sand (#FFBB98). Never use pure white (#FFFFFF).
                                    </p>
                                </div>
                                <div>
                                    <h3 className="font-medium mb-2" style={{ color: '#1D6B6B' }}>Primary Actions</h3>
                                    <p style={{ color: '#2A8585' }}>
                                        Use Sage Green (#7D8E95) or Eucalyptus (#2A8585) for main buttons and CTAs.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="font-medium mb-2" style={{ color: '#1D6B6B' }}>Secondary Actions</h3>
                                    <p style={{ color: '#2A8585' }}>
                                        Use Mist Blue (#7D8E95) for secondary buttons and less prominent actions.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="font-medium mb-2" style={{ color: '#1D6B6B' }}>Accents</h3>
                                    <p style={{ color: '#2A8585' }}>
                                        Use sparingly for emotional warmth. Dusty Lavender for communication, Muted Rose for co-regulation, Soft Gold for highlights.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="font-medium mb-2" style={{ color: '#1D6B6B' }}>Text</h3>
                                    <p style={{ color: '#2A8585' }}>
                                        Never use pure black. Use Deep Warm Grey (#1D6B6B) for headings and Soft Charcoal (#2A8585) for body text.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="font-medium mb-2" style={{ color: '#1D6B6B' }}>Avoid</h3>
                                    <ul className="list-disc list-inside space-y-1" style={{ color: '#2A8585' }}>
                                        <li>Pure white (#FFFFFF) and high-contrast black (#000000)</li>
                                        <li>Neon or saturated colors</li>
                                        <li>Harsh reds or intense colors</li>
                                        <li>Busy or high-contrast backgrounds</li>
                                    </ul>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </section>

                {/* Footer */}
                <footer className="text-center py-10" style={{ borderTop: '1px solid #FFBB98' }}>
                    <CoRegulateLogo size="sm" className="justify-center" />
                    <p className="text-sm mt-4" style={{ color: '#2A8585' }}>
                        Brand assets for CoRegulate
                    </p>
                </footer>
            </div>
        </div>
    );
};

export default BrandShowcase;
